import React, { useEffect, useState } from "react"

import { Box, Checkbox, Stack } from "@chakra-ui/react"

import isEqual from "lodash/isEqual"
import uniq from "lodash/uniq"

interface CheckboxOption {
  label: string
  value: string
  children?: CheckboxOption[]
}

interface Props {
  value: string[]
  options: CheckboxOption[]
  childrenWrapperStyle?: {
    pl?: number
    mt?: number
    spacing?: number
  }
  onChange: (value: string[]) => void
}

interface ViewProps {
  parentOption: CheckboxOption | null
  options: CheckboxOption[]
  selectedValues: string[]
  onChange: (selectedValues: string[]) => void
  childrenWrapperStyle?: {
    pl?: number
    mt?: number
    spacing?: number
  }
}

const getChildrenValues = (option: CheckboxOption): string[] => {
  if (!option?.children?.length) {
    return []
  }
  const result: string[] = []
  for (const child of option.children) {
    result.push(child.value, ...getChildrenValues(child))
  }
  return result
}

const isChecked = (
  option: CheckboxOption,
  checkedValues: string[],
): boolean => {
  if (option.children?.length) {
    return (
      Boolean(checkedValues.find((t) => t === option.value)) &&
      getChildrenValues(option).every((t) => checkedValues.find((k) => k === t))
    )
  }
  return Boolean(checkedValues.find((t) => t === option.value))
}

const getChildrenCheckedValues = (
  options: CheckboxOption[],
  checkedValues: string[],
): string[] => {
  const result = []
  for (const option of options) {
    if (option.children?.length) {
      if (checkedValues.includes(option.value)) {
        result.push(...getChildrenValues(option))
      } else {
        result.push(...getChildrenCheckedValues(option.children, checkedValues))
      }
    } else {
      if (checkedValues.includes(option.value)) {
        result.push(option.value)
      }
    }
  }
  return uniq(result)
}

const getParentCheckedValues = (
  options: CheckboxOption[],
  checkedValues: string[],
): string[] => {
  const result = []
  for (const option of options) {
    if (isChecked(option, checkedValues)) {
      result.push(option.value)
      continue
    }
    if (option.children?.length) {
      result.push(...getParentCheckedValues(option.children, checkedValues))
    }
  }
  return result
}

export const CheckboxTreeView = ({
  parentOption,
  options,
  selectedValues,
  onChange,
  childrenWrapperStyle,
}: ViewProps) => {
  useEffect(() => {
    let newCheckedIds = [...selectedValues]

    const p = options.every((t) => newCheckedIds.find((p) => p === t.value))
    if (parentOption?.value) {
      if (p) {
        newCheckedIds = uniq([...newCheckedIds, parentOption?.value])
      } else {
        newCheckedIds = newCheckedIds.filter((t) => t !== parentOption?.value)
      }
      newCheckedIds = newCheckedIds.sort()
      if (!isEqual(selectedValues, newCheckedIds)) onChange(newCheckedIds)
    }
  }, [options, parentOption?.value, selectedValues, onChange])

  return (
    <div>
      {options.map((option, index) => {
        const { label, value, children } = option
        return (
          <Box key={index}>
            {children && children.length ? (
              <>
                <Checkbox
                  id={value}
                  key={value}
                  isChecked={isChecked(option, selectedValues)}
                  isIndeterminate={
                    !isChecked(option, selectedValues) &&
                    getChildrenValues(option).some((t) =>
                      selectedValues.find((p) => p === t),
                    )
                  }
                  value={value}
                  size="md"
                  onChange={(e) => {
                    const childrenIds = getChildrenValues(option)
                    if (e.target.checked) {
                      const newSelectedIds = [...selectedValues, value]
                      newSelectedIds.push(...childrenIds)
                      onChange(uniq(newSelectedIds))
                    } else {
                      const newSelectedIds = selectedValues.filter(
                        (t) =>
                          t !== e.target.value &&
                          !childrenIds.find((id) => id === t),
                      )
                      onChange(newSelectedIds)
                    }
                  }}
                >
                  {label}
                </Checkbox>
                <Stack
                  {...(childrenWrapperStyle ?? { pl: 6, mt: 1, spacing: 1 })}
                >
                  <CheckboxTreeView
                    parentOption={option}
                    options={children}
                    selectedValues={selectedValues}
                    onChange={onChange}
                  />
                </Stack>
              </>
            ) : (
              <Stack>
                <Checkbox
                  id={value}
                  key={label}
                  value={value}
                  mb={0}
                  size="md"
                  isChecked={isChecked(option, selectedValues)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const newSelectedIds = uniq([...selectedValues, value])
                      onChange(newSelectedIds)
                    } else {
                      const newSelectedIds = selectedValues.filter(
                        (option) => option !== value,
                      )
                      onChange(newSelectedIds)
                    }
                  }}
                >
                  {label}
                </Checkbox>
              </Stack>
            )}
          </Box>
        )
      })}
    </div>
  )
}

export const CheckboxTree = ({
  value,
  options,
  childrenWrapperStyle,
  onChange,
}: Props) => {
  const [checkedValues, setCheckedValues] = useState<string[]>(
    getChildrenCheckedValues(options, value),
  )

  useEffect(() => {
    onChange(getParentCheckedValues(options, checkedValues))
  }, [checkedValues])

  return (
    <CheckboxTreeView
      options={options}
      parentOption={null}
      selectedValues={checkedValues}
      onChange={setCheckedValues}
      childrenWrapperStyle={childrenWrapperStyle}
    />
  )
}
